import * as React from "react";
import {cx}              from "emotion";
import {Link}            from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBuildingUser, faCircleUser
} from "@fortawesome/free-solid-svg-icons";

import "./menu.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {keyed}          from "@Common/react/keyed";
import {UseState}       from "@Common/react/use-state";
import {sort}           from "@Common/utils/collections";
import {chain}          from "@Common/utils/fs";

import {CompanyUserIcon}  from "../../../../common/company-user-icon";
import {getProfileLabels} from "../../../../common/get-profile-labels";
import {
    AGENT_LEVEL, BRANCH_LEVEL, COMPANY_LEVEL
}                         from "../../../../common/logic/user-level";

import {MenuHeaderDesktop} from "./menu-header-desktop";

export const Menu = ({active, navBlock}) => cs(
    consumeContext("accountMenu"),
    consumeContext("session"),
    consumeContext("company"),
    consumeContext("userLevels"),
    consumeContext("routerHistory"),
    ["profile", ({userLevels, session, company}, next) =>
    {
        const profileLabels = getProfileLabels({company, session, userLevels});
        return next({
            label: profileLabels?.profileName ?? "Loading...",
            subLabel: profileLabels?.orgName,
            icon: (
                userLevels.userLevel === COMPANY_LEVEL ? (
                    CompanyUserIcon({})
                ) : userLevels.userLevel === BRANCH_LEVEL ? (
                    <FontAwesomeIcon className="icon1" icon={faBuildingUser}/>
                ) : (
                    session?.data?.agent?.imageGallery?.headshots[0]?.thumbnailUrl ? (
                        <img
                            src={session?.data?.agent?.imageGallery?.headshots[0]?.thumbnailUrl}
                            alt={session?.data?.agentNameLast && session?.data?.agentNameFirst + " " + session?.data?.agentNameLast}
                        />
                    ) : (
                        <FontAwesomeIcon className="icon1" icon={faCircleUser}/>
                    )
                )
            )
        });
    }],
    ({accountMenu, userLevels, profile, routerHistory}) => (
        <div className="menu menu-070">
            <MenuHeaderDesktop profile={profile} />

            <div className="items">
                {chain(
                    userLevels.userLevel,
                    (level) => level === AGENT_LEVEL ? agentItems : level === BRANCH_LEVEL ? branchItems : companyItems,
                    (items) => !accountMenu ? items : items.filter((item) => accountMenu.hasOwnProperty(item.configPath)),
                    (items) => !accountMenu ? items : sort(items, (item) => accountMenu[item.configPath].linkIndex),
                    (items) => items.map((item) => {
                        const config = accountMenu?.[item.configPath] || item;
                        return cs(
                            keyed(item.configPath),
                            () => config.dropdownItems ? cs(
                                ["expanded", (_, next) => UseState({
                                    next,
                                    initValue: config.dropdownItems.some(item => item.configPath === active + "Menu"),
                                })],
                                ({expanded}) => <>
                                    <div
                                        className={cx("dropdown", {
                                            expanded: expanded.value,
                                            active: config.dropdownItems.some(item => item.configPath === active + "Menu")
                                        })}
                                        onClick={() => expanded.onChange(!expanded.value)}
                                        xd-qa-type="dropdown"
                                        xd-qa-name={config.linkLabel}
                                    >
                                        <FontAwesomeIcon className="menu-icon" icon={require("@fortawesome/free-solid-svg-icons")[config.icon]}/>
                                        <div className="label">
                                            {config.linkLabel}
                                        </div>
                                        <FontAwesomeIcon {...{
                                            className: "dropdown-icon",
                                            icon: require("@fortawesome/free-solid-svg-icons")["faCaretUp"],
                                            ...!expanded.value && {flip: "vertical"},

                                            "xd-qa-type": "icon",
                                            "xd-qa-name": "Dropdown " + (expanded.value ? "expanded" : "collapsed"),
                                        }}/>
                                    </div>
                                    {expanded.value && config.dropdownItems.map((dItem, i) => {
                                        const dItemConfig = accountMenu?.[item.configPath][i] || dItem;
                                        return cs(
                                            keyed(dItem.configPath),
                                            () => (
                                                <Link
                                                    className={cx("dropdown-item", {active: active + "Menu" === dItem.configPath})}
                                                    to={(dItemConfig.link) ?? "/"}
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        if (await navBlock?.block() === true) {
                                                            return;
                                                        }
                                                        routerHistory.push((dItemConfig.link) ?? "/");
                                                    }}
                                                    xd-qa-type="link"
                                                    xd-qa-name={dItemConfig.linkLabel}
                                                >
                                                    <FontAwesomeIcon icon={require("@fortawesome/free-solid-svg-icons")["faArrowRight"]}/>
                                                    <div className="label">
                                                        {dItemConfig.linkLabel}
                                                    </div>
                                                </Link>
                                            )
                                        );
                                    })}
                                </>
                            ) : (
                                <Link
                                    className={cx("item", {active: active + "Menu" === item.configPath})}
                                    to={(config.link) ?? "/"}
                                    xd-qa-type="link"
                                    xd-qa-name={config.linkLabel}
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        if (await navBlock?.block() === true) {
                                            return;
                                        }
                                        routerHistory.push((config.link) ?? "/");
                                    }}
                                >
                                    <FontAwesomeIcon icon={require("@fortawesome/free-solid-svg-icons")[config.icon]}/>
                                    <div className="label">
                                        {config.linkLabel}
                                    </div>
                                </Link>
                            ),
                        );
                    })
                )}
            </div>
        </div>
    ),
);

export const agentItems = [
    {
        linkLabel: "Profile",
        icon: "faUser",
        link: "/account/profile",
        configPath: "profileMenu",
        name: "profile",
    },
    {
        linkLabel: "Program Defaults",
        icon: "faCog",
        link: "/account/program-defaults",
        configPath: "programDefaultsDropdownMenu",
        name: "programDefaultsDropdown",
        dropdownItems: [
            {
                link: "/account/program-defaults/scheduled-campaigns",
                linkLabel: "Scheduled Campaign Preferences",
                configPath: "scheduledCampaignsMenu",
            }
        ],
    },
    {
        linkLabel: "Orders",
        icon: "faFolderOpen",
        link: "/account/orders/",
        configPath: "ordersMenu",
        name: "orders",
    },
    {
        linkLabel: "Wallet",
        icon: "faWallet",
        link: "/account/wallet/",
        configPath: "walletMenu",
        name: "wallet",
    },
    {
        linkLabel: "Address Book",
        icon: "faAddressBook",
        link: "/account/address-book",
        configPath: "addressBookMenu",
        name: "addressBook",
    },
    {
        linkLabel: "Analytics",
        icon: "faChartSimple",
        link: "/account/analytics",
        configPath: "analyticsMenu",
        name: "analytics",
    },
];

export const branchItems = [
    {
        linkLabel: "Office Account",
        icon: "faBuilding",
        link: "/account/office",
        configPath: "officeMenu",
        name: "office",
    },
    {
        linkLabel: "Branch Orders",
        icon: "faFolderOpen",
        link: "/account/orders/",
        configPath: "branchOrdersMenu",
        name: "branchOrders",
    },
    {
        linkLabel: "Admin Tools",
        icon: "faToolbox",
        link: "/account/admin",
        configPath: "adminDropdownMenu",
        name: "adminDropdown",
        dropdownItems: [
            {
                link: "/account/admin/user",
                linkLabel: "User Management",
                configPath: "userManagementMenu",
                name: "userManagement",
            },
            {
                link: "/account/admin/accounting",
                linkLabel: "Accounting",
                configPath: "accountingMenu",
                name: "accounting",
            },
            {
                link: "/account/admin/reports",
                linkLabel: "Reports",
                configPath: "reportsMenu",
                name: "reports",
            },
            {
                link: "/account/admin/listing-recipient-admin",
                linkLabel: "Listing/Recipient Admin",
                configPath: "listingsAdminMenu",
                name: "listingsAdmin"
            }
        ]
    },
    {
        linkLabel: "My Profile",
        icon: "faUser",
        link: "/account/my-profile",
        configPath: "myProfileMenu",
        name: "myProfile",
    },
    {
        linkLabel: "Analytics",
        icon: "faChartSimple",
        link: "/account/analytics",
        configPath: "analyticsMenu",
        name: "analytics",
    },
];

export const companyItems = [
    {
        linkLabel: "Company Account",
        icon: "faBuilding",
        link: "/account/company",
        configPath: "companyMenu",
        name: "company",
    },
    {
        linkLabel: "Company Orders",
        icon: "faFolderOpen",
        link: "/account/orders/",
        configPath: "companyOrdersMenu",
        name: "companyOrders",
    },
    {
        linkLabel: "Admin Tools",
        icon: "faToolbox",
        link: "/account/admin",
        configPath: "adminDropdownMenu",
        name: "adminDropdown",
        dropdownItems: [
            {
                link: "/account/admin/office",
                linkLabel: "Office Management",
                configPath: "officeManagementMenu",
                name: "officeManagement",
            },
            {
                link: "/account/admin/user-management",
                linkLabel: "User Management",
                configPath: "userManagementMenu",
                name: "userManagement",
            },
            {
                link: "/account/admin/accounting",
                linkLabel: "Accounting",
                configPath: "accountingMenu",
                name: "accounting",
            },
            {
                link: "/account/admin/reports",
                linkLabel: "Reports",
                configPath: "reportsMenu",
                name: "reports",
            },
            {
                link: "/account/admin/listing-recipient-admin",
                linkLabel: "Listing/Recipient Admin",
                configPath: "listingsAdminMenu",
                name: "listingsAdmin",
            }
        ]
    },
    {
        linkLabel: "My Profile",
        icon: "faUser",
        link: "/account/my-profile",
        configPath: "myProfileMenu",
        name: "myProfile",
    },
    {
        linkLabel: "Analytics",
        icon: "faChartSimple",
        link: "/account/analytics",
        configPath: "analyticsMenu",
        name: "analytics",
    },
];
